import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { inr } from "@/lib/utils-format";
import { CheckCircle2, TrendingUp, TrendingDown, Package, Boxes, ArrowDownUp, Search } from "lucide-react";

// ==============================================================
// Complete-batch dialog — used from the Batches table
// ==============================================================
export function CompleteBatchDialog({ batch, open, onOpenChange, onCompleted }) {
  const [labour, setLabour] = useState(0);
  const [power, setPower] = useState(0);
  const [packing, setPacking] = useState(0);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) { setLabour(0); setPower(0); setPacking(0); setNotes(""); }
  }, [open]);

  const total = useMemo(() => {
    const ing = Number(batch?.ingredient_cost || 0);
    return Number(ing) + Number(labour || 0) + Number(power || 0) + Number(packing || 0);
  }, [batch, labour, power, packing]);

  const cpu = useMemo(() => {
    const q = Number(batch?.quantity || 0);
    return q > 0 ? total / q : 0;
  }, [total, batch]);

  const submit = async () => {
    if (!batch) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/production/batches/${batch.id}/complete`, {
        labour_cost: Number(labour || 0),
        power_cost: Number(power || 0),
        packing_cost: Number(packing || 0),
        notes: notes || null,
      });
      toast.success(`Batch ${batch.batch_no} completed — cost/unit ${inr(data.cost_per_unit)}`);
      onOpenChange(false);
      onCompleted?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to complete batch");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-white" data-testid="batch-complete-dialog">
        <DialogHeader>
          <DialogTitle className="font-[Outfit] flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-emerald-600" />
            Mark Batch Completed
          </DialogTitle>
          <DialogDescription className="text-xs text-[#8B7355]">
            Add labour, power and packing costs to close this batch. Raw-material cost is already captured from the recipe.
          </DialogDescription>
        </DialogHeader>

        {batch && (
          <div className="space-y-3 py-2">
            <div className="bg-[#FAF6EE] border border-[#EAE5D9] rounded-md p-3 text-sm">
              <div className="font-mono text-xs text-[#8B7355]">{batch.batch_no}</div>
              <div className="font-medium">{batch.product_name} <span className="text-xs text-[#8B7355]">({batch.product_sku})</span></div>
              <div className="text-xs text-[#8B7355] mt-1">Quantity produced: <b className="text-slate-800">{batch.quantity}</b></div>
              <div className="text-xs text-[#8B7355]">Raw material cost (from recipe): <b className="text-slate-800">{inr(batch.ingredient_cost || 0)}</b></div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Labour ₹</Label>
                <Input type="number" value={labour} onChange={(e) => setLabour(e.target.value)} data-testid="batch-complete-labour" min={0} />
              </div>
              <div>
                <Label>Power ₹</Label>
                <Input type="number" value={power} onChange={(e) => setPower(e.target.value)} data-testid="batch-complete-power" min={0} />
              </div>
              <div>
                <Label>Packing ₹</Label>
                <Input type="number" value={packing} onChange={(e) => setPacking(e.target.value)} data-testid="batch-complete-packing" min={0} />
              </div>
            </div>

            <div>
              <Label>Notes (optional)</Label>
              <Input value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="batch-complete-notes" placeholder="e.g. Extra shift, overtime, faulty oven…" />
            </div>

            <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3 flex justify-between items-center">
              <div>
                <div className="text-xs uppercase tracking-wide text-emerald-800">Total manufacturing cost</div>
                <div className="font-[Outfit] text-2xl font-semibold text-emerald-800">{inr(total)}</div>
              </div>
              <div className="text-right">
                <div className="text-xs uppercase tracking-wide text-emerald-800">Per unit</div>
                <div className="font-[Outfit] text-xl font-semibold text-emerald-800">{inr(cpu)}</div>
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="batch-complete-save" className="bg-emerald-600 hover:bg-emerald-700 text-white">
            {busy ? "Saving…" : "Mark Completed"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ==============================================================
// Batch Profitability panel — Selling Price − Manufacturing Cost = Gross Profit
// ==============================================================
export function BatchProfitabilityPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== "all") params.status = statusFilter;
      const { data } = await api.get("/production/profitability", { params });
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load profitability");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  const rows = (data?.rows || []).filter((r) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (r.batch_no || "").toLowerCase().includes(q) ||
           (r.product_name || "").toLowerCase().includes(q) ||
           (r.product_sku || "").toLowerCase().includes(q);
  });
  const t = data?.totals || {};

  return (
    <div className="space-y-4" data-testid="batch-profitability-panel">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <Label>Status</Label>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-44" data-testid="prof-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">All batches</SelectItem>
              <SelectItem value="completed">Completed only</SelectItem>
              <SelectItem value="active">Active only</SelectItem>
              <SelectItem value="recalled">Recalled</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1 min-w-[240px]">
          <Label>Search</Label>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-[#8B7355]" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} className="pl-8" placeholder="Batch # / SKU / product…" data-testid="prof-search" />
          </div>
        </div>
        <div className="ml-auto text-xs text-[#8B7355]">
          {loading ? "Loading…" : `${rows.length} of ${t.batches ?? 0} batches`}
        </div>
      </div>

      {/* Totals cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white border border-[#EAE5D9] rounded-lg p-4">
          <div className="uc-label">Total manufacturing cost</div>
          <div className="font-[Outfit] text-xl font-semibold mt-1">{inr(t.total_cost)}</div>
        </div>
        <div className="bg-white border border-[#EAE5D9] rounded-lg p-4">
          <div className="uc-label">Theoretical GP (MRP × qty)</div>
          <div className={`font-[Outfit] text-xl font-semibold mt-1 ${(t.theoretical_profit || 0) >= 0 ? "text-emerald-700" : "text-red-700"}`}>
            {inr(t.theoretical_profit)}
          </div>
          <div className="text-[10px] text-[#8B7355] mt-1">Revenue {inr(t.theoretical_revenue)}</div>
        </div>
        <div className="bg-white border border-[#EAE5D9] rounded-lg p-4">
          <div className="uc-label">Actual GP (POS-sold)</div>
          <div className={`font-[Outfit] text-xl font-semibold mt-1 ${(t.actual_profit || 0) >= 0 ? "text-emerald-700" : "text-red-700"}`}>
            {inr(t.actual_profit)}
          </div>
          <div className="text-[10px] text-[#8B7355] mt-1">Revenue {inr(t.actual_revenue)}</div>
        </div>
        <div className="bg-white border border-[#EAE5D9] rounded-lg p-4">
          <div className="uc-label">Batches</div>
          <div className="font-[Outfit] text-xl font-semibold mt-1">{t.batches ?? 0}</div>
        </div>
      </div>

      <div className="bg-white border border-[#EAE5D9] rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left uc-label border-b border-[#EAE5D9]">
              <th className="px-4 py-3">Batch</th>
              <th className="px-4 py-3">Product</th>
              <th className="px-4 py-3 text-right">Qty</th>
              <th className="px-4 py-3 text-right">Cost/unit</th>
              <th className="px-4 py-3 text-right">Total cost</th>
              <th className="px-4 py-3 text-right">Theoretical GP</th>
              <th className="px-4 py-3 text-right">Sell-thru</th>
              <th className="px-4 py-3 text-right">Actual GP</th>
              <th className="px-4 py-3">Status</th>
            </tr>
          </thead>
          <tbody data-testid="prof-rows">
            {rows.map((r) => (
              <tr key={r.batch_id} className="border-b border-[#EAE5D9] last:border-0 hover:bg-[#FDFBF7]" data-testid={`prof-row-${r.batch_id}`}>
                <td className="px-4 py-2 font-mono text-xs">{r.batch_no}</td>
                <td className="px-4 py-2">{r.product_name} <span className="text-[10px] text-[#8B7355]">{r.product_sku}</span></td>
                <td className="px-4 py-2 text-right">{r.quantity_produced}</td>
                <td className="px-4 py-2 text-right font-mono text-xs">{inr(r.cost_per_unit)}</td>
                <td className="px-4 py-2 text-right font-semibold text-red-700">{inr(r.total_cost)}</td>
                <td className={`px-4 py-2 text-right font-semibold ${r.theoretical.gross_profit >= 0 ? "text-emerald-700" : "text-red-700"}`}>
                  {inr(r.theoretical.gross_profit)}
                  <div className="text-[10px] text-[#8B7355] font-normal">{r.theoretical.margin_pct}%</div>
                </td>
                <td className="px-4 py-2 text-right">
                  <span className="text-xs">{r.actual.sell_through_pct}%</span>
                  <div className="text-[10px] text-[#8B7355]">{r.actual.quantity_sold} sold</div>
                </td>
                <td className={`px-4 py-2 text-right font-semibold ${r.actual.gross_profit >= 0 ? "text-emerald-700" : "text-red-700"}`}>
                  {inr(r.actual.gross_profit)}
                  <div className="text-[10px] text-[#8B7355] font-normal">{r.actual.margin_pct}%</div>
                </td>
                <td className="px-4 py-2">
                  {r.status === "completed" ? <Badge className="bg-emerald-600 text-white">Completed</Badge>
                    : r.status === "recalled" ? <Badge className="bg-red-600 text-white">Recalled</Badge>
                    : <Badge variant="outline">{r.status || "active"}</Badge>}
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={9} className="text-center py-10 text-[#8B7355]">{loading ? "Loading…" : "No batches match this filter"}</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ==============================================================
// Inventory Ledger panel — Stock In / Stock Out per SKU
// ==============================================================
export function InventoryLedgerPanel() {
  const [itemType, setItemType] = useState("product");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);   // item detail row
  const [detail, setDetail] = useState(null);       // movement list for selected

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/inventory/ledger", { params: { item_type: itemType } });
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load ledger");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); setSelected(null); setDetail(null); /* eslint-disable-next-line */ }, [itemType]);

  const loadDetail = async (row) => {
    setSelected(row);
    try {
      const { data } = await api.get("/inventory/ledger", { params: { item_type: itemType, item_id: row.item_id } });
      setDetail(data);
    } catch (e) { toast.error("Failed to load movements"); }
  };

  const rows = (data?.summary || []).filter((r) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (r.item_name || "").toLowerCase().includes(q) || (r.sku || "").toLowerCase().includes(q);
  });
  const t = data?.totals || {};

  return (
    <div className="space-y-4" data-testid="inventory-ledger-panel">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <Label>Item type</Label>
          <Select value={itemType} onValueChange={setItemType}>
            <SelectTrigger className="w-44" data-testid="ledger-type"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="product">Finished SKUs</SelectItem>
              <SelectItem value="ingredient">Ingredients</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1 min-w-[240px]">
          <Label>Search</Label>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-[#8B7355]" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} className="pl-8" placeholder="Name or SKU…" data-testid="ledger-search" />
          </div>
        </div>
        <div className="ml-auto text-xs text-[#8B7355]">
          {loading ? "Loading…" : `${rows.length} of ${t.skus ?? 0} SKUs · IN ${inr(t.total_in)} · OUT ${inr(t.total_out)}`}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-white border border-[#EAE5D9] rounded-lg overflow-x-auto lg:col-span-2">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left uc-label border-b border-[#EAE5D9]">
                <th className="px-4 py-3">SKU / Name</th>
                <th className="px-4 py-3 text-right">Stock In</th>
                <th className="px-4 py-3 text-right">Stock Out</th>
                <th className="px-4 py-3 text-right">Net</th>
                <th className="px-4 py-3 text-right">Current</th>
              </tr>
            </thead>
            <tbody data-testid="ledger-rows">
              {rows.map((r) => (
                <tr key={r.item_id}
                    onClick={() => loadDetail(r)}
                    className={`border-b border-[#EAE5D9] last:border-0 cursor-pointer hover:bg-[#FDFBF7] ${selected?.item_id === r.item_id ? "bg-[#FEEBC8]" : ""}`}
                    data-testid={`ledger-row-${r.item_id}`}>
                  <td className="px-4 py-2">
                    <div className="font-mono text-[10px] text-[#8B7355]">{r.sku}</div>
                    <div className="text-sm">{r.item_name}</div>
                  </td>
                  <td className="px-4 py-2 text-right font-semibold text-emerald-700">{r.stock_in}</td>
                  <td className="px-4 py-2 text-right font-semibold text-red-700">{r.stock_out}</td>
                  <td className={`px-4 py-2 text-right font-semibold ${r.net >= 0 ? "text-slate-800" : "text-red-700"}`}>{r.net}</td>
                  <td className="px-4 py-2 text-right">{r.current_stock ?? "—"}</td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={5} className="text-center py-10 text-[#8B7355]">{loading ? "Loading…" : "No stock movements found"}</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="bg-white border border-[#EAE5D9] rounded-lg p-4 min-h-[240px]" data-testid="ledger-detail">
          {!selected ? (
            <div className="text-center text-[#8B7355] text-sm mt-6">
              <ArrowDownUp className="w-8 h-8 mx-auto mb-2 opacity-40" />
              Click a SKU to see its movement history.
            </div>
          ) : (
            <div>
              <div className="uc-label mb-1">Ledger for</div>
              <div className="font-[Outfit] text-lg font-semibold">{selected.item_name}</div>
              <div className="font-mono text-[10px] text-[#8B7355] mb-3">{selected.sku}</div>
              <div className="max-h-[400px] overflow-auto -mx-2 px-2">
                {(detail?.movements || []).map((m) => (
                  <div key={m.id} className="flex items-start gap-2 text-xs border-b border-[#EAE5D9] py-2 last:border-0">
                    <div className={`w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center ${m.quantity >= 0 ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
                      {m.quantity >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                    </div>
                    <div className="flex-1">
                      <div className="flex justify-between">
                        <span className="capitalize">{m.movement_type}</span>
                        <span className={`font-semibold ${m.quantity >= 0 ? "text-emerald-700" : "text-red-700"}`}>{m.quantity > 0 ? "+" : ""}{m.quantity}</span>
                      </div>
                      {m.reference && <div className="font-mono text-[10px] text-[#8B7355]">Ref: {m.reference}</div>}
                      {m.notes && <div className="text-[10px] text-[#8B7355] truncate">{m.notes}</div>}
                      <div className="text-[10px] text-[#8B7355]">{(m.created_at || "").slice(0, 16).replace("T", " ")}</div>
                    </div>
                  </div>
                ))}
                {(!detail || detail?.movements?.length === 0) && (
                  <div className="text-xs text-[#8B7355] text-center py-4">No individual movements.</div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
